import type { InspectClass } from './index';

/**
 * The bottom of the confidence scale, for a workflow that names no confidence
 * of its own. Zero: a graph that filters nothing has discarded nothing, so
 * there is no lower bound to inherit from it.
 */
export const INSPECT_DEFAULT_THRESHOLD = 0;

/**
 * The top of the confidence scale. A score is a probability, so nothing sits
 * above it, and it is the upper end of every class's slider.
 */
export const INSPECT_MAX_THRESHOLD = 1;

/**
 * Where a class with no threshold of its own is filtered: halfway between the
 * confidence the workflow already applies and {@link INSPECT_MAX_THRESHOLD}.
 *
 * Midway rather than at the floor, so the slider a class starts on has room to
 * move in both directions. Starting hard against the bottom of its own track
 * offers only tightening, and gives the operator no way to see that loosening
 * is a thing the control does.
 *
 * It follows the floor rather than being a fixed 50%, because the useful half of
 * the range is the half the graph has not already cut away. On a workflow
 * filtering at 40% a fixed midpoint would sit a fifth of the way up what is
 * left.
 */
export function inspectMidpointThreshold(workflowFloor?: number): number {
  const floor = workflowFloor ?? INSPECT_DEFAULT_THRESHOLD;

  return floor + (INSPECT_MAX_THRESHOLD - floor) / 2;
}

/**
 * The threshold one class is actually filtered at: its own value, else
 * {@link inspectMidpointThreshold}.
 *
 * It never inherits another class's value. Doing so would mean raising one class
 * to 90% silently tightened every class the operator had deliberately left
 * alone, and in a defect count that reads as the model missing defects rather
 * than as a setting somebody made.
 *
 * A threshold the operator set is returned as given, with no clamp to the
 * workflow's own. Going below that gains nothing, since the graph discarded
 * those detections before we saw them, but clamping upward misreported what the
 * operator had chosen. The slider bounds its own track at the floor instead, so
 * a value below one only survives on a class configured before the workflow
 * raised it.
 */
export function inspectEffectiveThreshold(
  defectClass: InspectClass,
  workflowFloor?: number,
): number {
  return (
    defectClass.confidenceThreshold ?? inspectMidpointThreshold(workflowFloor)
  );
}

/**
 * The single confidence sent to the workflow: the workflow's own floor.
 *
 * Deliberately not derived from the per-class thresholds. Per-class filtering
 * happens on our side after a raw inference, so the strictest per-class value
 * would narrow the set a second time, and any non-zero value would drop a class
 * republished into the workflow since Import before `inspectDetectionPasses`
 * could apply its keep-an-unlisted-class rule.
 */
export function inspectThresholdFloor(workflowFloor?: number): number {
  return workflowFloor ?? INSPECT_DEFAULT_THRESHOLD;
}

/**
 * Whether a detection clears the threshold for its own class.
 *
 * A detection whose class name is not in the list is **kept**. The list comes
 * from the model's own class names, so a name missing from it means the model
 * emitted something the imported list does not describe — a workflow
 * republished with a new class since Import. Counting it is the safe direction:
 * dropping it would hide a defect type entirely, while counting it leaves the
 * box on the frame with its name on it, which is visible and diagnosable.
 */
export function inspectDetectionPasses(
  className: string | undefined,
  score: number,
  classes: readonly InspectClass[],
  workflowFloor?: number,
): boolean {
  const matched = classes.find(
    (defectClass) => defectClass.label === className,
  );

  if (!matched) return true;

  return score >= inspectEffectiveThreshold(matched, workflowFloor);
}

/** `0.63` → `63%`, for the inline threshold on a class row. */
export function formatInspectThreshold(threshold: number): string {
  return `${Math.round(threshold * 100)}%`;
}

/**
 * The per-class detection cap Inspect sends to the workflow, and the flag that
 * asks for it per class.
 *
 * Both live here, beside the threshold rules, because **two callers must send
 * them identically**: the wizard's live preview
 * (`inspectPreviewArgs.ts`) and the routine step (`RunInspectSkill/Step.ts`).
 * They were duplicated in both files, each with a comment saying it had to
 * match the other — which is a convention, not a guarantee, and it failed once
 * already: the preview was left capping globally while the step capped per
 * class, so the number an operator tuned a threshold against was not the number
 * the routine published.
 *
 * `max_detections_per_class` has to be asked for because vision-bot defaults to
 * a global slice, keeping `max_detections` meaning one thing for a Locate skill
 * across every model source (its checkpoint backends slice globally too).
 *
 * The cap is kept well above any plausible defect count. A truncated result set
 * is the silent-undercount hazard for Inspect specifically: the verdict is
 * derived from these counts, so a cap reached is a defect not reported, with no
 * error raised anywhere. Per class, a prolific class also cannot evict a quiet
 * one.
 */
export const INSPECT_THRESHOLDING_CAPS = {
  max_detections: '300',
  max_detections_per_class: 'true',
} as const;
